import React, { useState } from 'react';
import Slider from 'react-slick';
import css from './Sections.module.css';
import '../../../styles/slick/slick.css';
import '../../../styles/slick/slick-theme.css';
import classNames from 'classnames';
import { isArrayLength, isVideoFile } from '../../../util/genericHelpers';
import { SectionUnlockableTab } from '../../../util/enums';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { useConfiguration } from '../../../context/configurationContext';
import { formatMoney } from '../../../util/currency';
import ExternalLink from '../../../components/ExternalLink/ExternalLink';
import { useSelector } from 'react-redux';
import { getUserType } from '../../../util/userDataExtraction';
import SubscriptionPaymentModal from '../../SubscriptionPage/SubscriptionPaymentModal';
import { Button } from '../../../components';
import { selectIsAuthenticated } from '../../../ducks/auth.duck';
import { userSelector } from '../../../ducks/user.duck';
import { createResourceLocatorString } from '../../../util/routes';
import { createSlug } from '../../../util/urlHelpers';
import { setListingSessionUrl } from '../../../util/helperFunction';
import { isCancellationDateInFuture } from '../../../util/subscriptionHelpers';
import { getDaysUntilExpiration, isContentExpired } from '../../../util/listingPageHelpers';

const { Money } = sdkTypes;

const TABS = ['Photos', 'Videos', 'Posts'];

const sliderSettings = {
  dots: false,
  arrows: true,
  infinite: false,
  centerMode: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  responsive: [
    { breakpoint: 1300, settings: { slidesToShow: 2 } },
    { breakpoint: 991, settings: { slidesToShow: 2 } },
    { breakpoint: 767, settings: { slidesToShow: 1 } },
    { breakpoint: 480, settings: { slidesToShow: 1 } },
  ],
};

const Step = ({
  item,
  intl,
  openPaymentModal,
  listing,
  activeTab,
  currentListingUnlockedPurchases,
  history,
  routeConfiguration,
  isAuthenticated,
  isOwnListing,
  isCurrentUserSubscriptionActive,
  hasUnlockableContentAccess,
  isCancellationDateValid,
  willCancelAtPeriodEnd,
}) => {
  // Use in Step component to show expiration warning:
  const daysUntilExpiration = getDaysUntilExpiration(item?.expirationDate);

  // ============================================
  // Check if subscription is active but pending cancellation
  // ============================================
  const isSubscriptionPendingCancellation = willCancelAtPeriodEnd && isCancellationDateValid;

  // Check if user has both active subscription AND unlockable content access

  const canAccessUnlockableContent =
    hasUnlockableContentAccess &&
    (willCancelAtPeriodEnd
      ? isSubscriptionPendingCancellation // If cancelled, must be pending + valid
      : isCurrentUserSubscriptionActive); // If not cancelled, subscription must be active
  const isVideo = isVideoFile(item.name);
  const { currency } = useConfiguration();
  const priceCents = item.price ? item.price * 100 : 0;
  const priceObject = item.price && new Money(Number(priceCents), currency);
  const formattedPrice = item.price && formatMoney(intl, priceObject);
  const links = item.links || [];

  const isPurchased = currentListingUnlockedPurchases.some(
    purchase =>
      purchase.listingId === listing?.id?.uuid &&
      purchase.media?.[activeTab]?.some(i => i.mediaKey === item.key)
  );

  const listingId = listing?.id?.uuid;
  const listingTitle = listing?.attributes?.title;
  const slug = createSlug(listingTitle);
  const handleBuyNow = () => {
    try {
      if (isAuthenticated) {
        openPaymentModal({
          priceAmount: item?.price || 0,
          itemName: item?.title || '',
          itemToBuy: item,
          listingId: listing?.id?.uuid,
          activeTab,
        });
      } else {
        history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}));
        setListingSessionUrl(
          createResourceLocatorString(
            'ListingPage',
            routeConfiguration,
            { slug, id: listingId },
            {}
          )
        );
      }
    } catch (error) {}
  };

  // Check if content should be visible (unlocked)
  const hasContentAccess = isPurchased || isOwnListing || canAccessUnlockableContent;

  // Determine CSS class based on access
  const contentBlockClass = hasContentAccess ? css.unlockableBlockImg : css.blurUnlockableBlockImg;

  const ACCESS_STATES = {
    PURCHASED: isPurchased,
    OWN_LISTING: isOwnListing,
    SUBSCRIPTION_ACCESS: canAccessUnlockableContent,
  };
  const hasAnyAccess = Object.values(ACCESS_STATES).some(access => access);
  const shouldShowBuyOverlay = !hasAnyAccess;
  const hasLinksToDisplay = isArrayLength(links);
  const userHasLinkAccess = hasContentAccess;
  const shouldShowLinks = hasLinksToDisplay && userHasLinkAccess;
  const showTitle = item?.title && hasContentAccess;
  const showContent = item?.content && hasContentAccess;
  return (
    <div className={css.unlockableBlock}>
      {shouldShowBuyOverlay && (
        <div className={css.buyNowOverlay}>
          <Button className={css.buyNowButton} onClick={handleBuyNow}>
            {intl.formatMessage({ id: 'SectionUnlockable.buyNowBtn' })}
          </Button>
        </div>
      )}
      <div className={contentBlockClass}>
        {isVideo ? (
          <video autoPlay loop muted playsInline src={item.fileLink} />
        ) : (
          <img src={item.fileLink} alt={item.name || 'media'} />
        )}
      </div>
      <div className={css.unlockableBlockInfo}>
        {showTitle && <h3>{item.title}</h3>}
        {showContent && <p>{item.content}</p>}
        {(item.price && isPurchased) || canAccessUnlockableContent ? (
          <p>{intl.formatMessage({ id: 'SectionUnlockable.unLocked' })}</p>
        ) : (
          <p>{formattedPrice}</p>
        )}
        {shouldShowLinks && (
          <div className={css.unlockableBlockLinks}>
            {links.map((link, idx) => (
              <ExternalLink key={idx} href={link}>
                {link}
              </ExternalLink>
            ))}
          </div>
        )}
      </div>
      {daysUntilExpiration && daysUntilExpiration <= 7 && (
        <p className={css.expirationWarning}>
          {intl.formatMessage(
            { id: 'SectionUnlockable.expiringIn' },
            { days: daysUntilExpiration }
          )}
        </p>
      )}
    </div>
  );
};

const SectionUnlockable = ({
  intl,
  publicData,
  listing,
  history,
  routeConfiguration,
  isOwnListing,
  isCurrentUserSubscriptionActive,
  hasUnlockableContentAccess,
  cancellationDateUnix,
  willCancelAtPeriodEnd,
}) => {
  const isCancellationDateValid = isCancellationDateInFuture(cancellationDateUnix);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [activeTab, setActiveTab] = useState(SectionUnlockableTab.PHOTOS);
  const [paymentModalState, setPaymentModalState] = useState(null);
  const { formatMessage } = intl;
  const currentUser = useSelector(userSelector);
  const userType = getUserType(currentUser);
  const currentUserUnlockedPurchases =
    currentUser?.attributes?.profile?.protectedData?.unlockedPurchases || [];

  const currentListingUnlockedPurchases = currentUserUnlockedPurchases?.filter(
    item => item?.listingId === listing?.id?.uuid
  );

  const unlockableContent = publicData?.unlockableContent || {};
  const getItemsForTab = () => {
    const sectionData = unlockableContent[activeTab.toLowerCase()];
    if (!Array.isArray(sectionData)) return [];

    return sectionData.flatMap(item => {
      const files = item.files || item?.photo || [];
      return files
        .filter(file => {
          // Check if content is expired
          if (isContentExpired(item?.expirationDate)) {
            return false; // Filter out expired content
          }

          if (activeTab === SectionUnlockableTab.VIDEOS) return isVideoFile(file.name);
          if (activeTab === SectionUnlockableTab.POSTS) return true; // allow all for Posts
          if (activeTab === SectionUnlockableTab.PHOTOS) return !isVideoFile(file.name); // only images
          return false;
        })
        .map(file => ({
          ...file,
          content: item.content,
          title: item.title,
          price: item?.price,
          links: item?.links,
          photo: item?.photo,
          expirationDate: item?.expirationDate,
        }));
    });
  };

  const currentItems = getItemsForTab();
  const openPaymentModal = modalData => {
    setPaymentModalState(modalData);
  };

  if (!Object.keys(unlockableContent).length) return null;

  return (
    <div className={classNames(css.howItWorks, css.unlockableSec)}>
      <div className={css.contentWidth}>
        <div className={css.sectionHead}>
          <div className={css.leftSec}>
            <h2>{formatMessage({ id: 'SectionUnlockable.heading' })}</h2>
            <p>{formatMessage({ id: 'SectionUnlockable.description' })}</p>
          </div>
        </div>

        <div className={css.contentHeroTabs}>
          <div className={css.tabs}>
            {TABS.map(tab => (
              <span
                key={tab}
                className={activeTab === tab ? css.active : ''}
                onClick={() => setActiveTab(tab)}
              >
                {formatMessage({ id: `SectionUnlockable.${tab.toLowerCase()}` })}
              </span>
            ))}
          </div>
        </div>

        <div className={css.sectionContent}>
          <div className={css.featuredListingsContent}>
            <div className={css.templatesSlider}>
              {currentItems.length > 0 ? (
                <Slider {...sliderSettings}>
                  {currentItems.map((item, idx) => (
                    <Step
                      key={idx}
                      item={item}
                      intl={intl}
                      openPaymentModal={openPaymentModal}
                      listing={listing}
                      activeTab={activeTab}
                      currentListingUnlockedPurchases={currentListingUnlockedPurchases}
                      isAuthenticated={isAuthenticated}
                      history={history}
                      routeConfiguration={routeConfiguration}
                      isOwnListing={isOwnListing}
                      hasUnlockableContentAccess={hasUnlockableContentAccess}
                      isCurrentUserSubscriptionActive={isCurrentUserSubscriptionActive}
                      isCancellationDateValid={isCancellationDateValid}
                      willCancelAtPeriodEnd={willCancelAtPeriodEnd}
                    />
                  ))}
                </Slider>
              ) : (
                <div className={css.noContent}>
                  {formatMessage({ id: 'SectionUnlockable.noContent' })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <SubscriptionPaymentModal
        isOpen={!!paymentModalState?.priceAmount}
        setPaymentMethodModalState={setPaymentModalState}
        selectedPlanId={null}
        selectedPlanName={paymentModalState?.itemName}
        subscriptionType={paymentModalState?.itemType}
        currentUser={currentUser}
        activeSubscriptionPlanId={null}
        shouldUpdate={false}
        subscriptionId={null}
        userType={userType}
        setIsConfirmationModalOpen={() => {
          setPaymentModalState(null);
        }}
        paymentModalState={paymentModalState}
        fromSectionUnlockable={true}
      />
    </div>
  );
};

export default SectionUnlockable;
